import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useStore } from 'effector-react'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Table, TableProps, Tag, message } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/lib/table'
import { transactionRequestDirection } from 'src/pages/billing/const'
import {
  path,
  role,
  useColumnLocalFilter,
  useColumnLocalSort,
  useColumnSearch,
  useTableSearchParams,
} from 'src/libs'
import { $userRole } from 'src/features/user'
import { ErrorScreen } from 'src/shared/components/errorScreen/errorScreen'
import { Guard } from 'src/features/session/guard'
import { formatDigits } from 'src/libs/utils'
import {
  setMoneyHistoriesRequestError,
  getMoneyHistoryStatuses,
  $moneyHistoryStatuses,
} from './model'
import { GeneralFilters, NovaFilters } from './ constants'
import { useMoneyHistoriesList } from './hooks'
import { MoneyHistory, MoneyHistoryType } from './types'
import moment from 'moment'
import {
  resizableTitle,
  TableColumnOption,
  useTableColumnSettings,
} from 'src/components/tableColumnSettings'

type Props = {
  entityId: string
  type?: MoneyHistoryType
  client?: {
    name?: string
    email?: string
    country?: string
  }
}
const getOperationLink = (item: MoneyHistory): string => {
  switch (item.operationType) {
    case 'nova_sale':
    case 'transaction':
      return item.direction === transactionRequestDirection.In()
        ? path.billing.deposit.edit(item.operationId)
        : path.billing.withdrawal.edit(item.operationId)
    case 'deposit':
    case 'nova_deposit':
      return path.billing.deposit.edit(item.operationId)
    case 'nova_withdrawal':
    case 'withdrawal':
      return path.billing.withdrawal.edit(item.operationId)
    case 'internal_transfer':
      return path.billing.internalTransfer.entry()
    case 'exchanger_transfer':
      return path.billing.exchangerTransfer.details(item.operationId)
    default:
      return ''
  }
}

const getOperationDetails = (item: MoneyHistory): string => {
  if (
    !['internal_resale_buy', 'internal_resale_sell'].includes(
      item.operationType,
    )
  ) {
    return ''
  }

  try {
    const details = JSON.parse(
      item.operationType === 'internal_resale_buy'
        ? item.fromName
        : item.toName,
    ) as Record<string, string | number | undefined>

    return [
      details.sell_offer_id && `Sell Offer ID: ${details.sell_offer_id}`,
      details.sell_offer_sell_id &&
        `Sell Payout ID: ${details.sell_offer_sell_id}`,
      details.sell_offer_buy_id &&
        `Hot Buy Order ID: ${details.sell_offer_buy_id}`,
    ]
      .filter(Boolean)
      .join(', ')
  } catch {
    return ''
  }
}

export const MoneyHistoryList = ({ entityId, type, client }: Props) => {
  const [searchParams] = useSearchParams()
  const {
    moneyHistories,
    totalCountOfMoneyHistories,
    loading,
    onChangeMoneyHistoriesList,
    errorMoneyHistoriesRequest,
  } = useMoneyHistoriesList()

  const userRole = useStore($userRole)
  const moneyHistoryStatuses = useStore($moneyHistoryStatuses)

  const getFilters = (): Record<string, string | string[]> => {
    const filters: Record<string, string | string[]> = {}

    const operationId = searchParams.get('searchField_operationId')
    if (operationId) {
      filters['operationId'] = operationId
    }

    const createdAt = searchParams.get('searchField_humanCreatedAt')
    if (createdAt) {
      filters['humanCreatedAt'] = createdAt
    }

    const updatedAt = searchParams.get('searchField_humanUpdatedAt')
    if (updatedAt) {
      filters['humanUpdatedAt'] = updatedAt
    }

    const operationType = searchParams.get('filterField_operationType')
    if (operationType) {
      filters['operationType'] = operationType.split(',')
    }

    const operationStatus = searchParams.get('filterField_operationStatus')
    if (operationStatus) {
      filters['operationStatus'] = operationStatus.split(',')
    }

    return filters
  }

  const searchFields = ['operationId', 'humanCreatedAt', 'humanUpdatedAt']
  const filterFields = ['operationType', 'operationStatus']
  const sorterFields = ['operationId', 'humanUpdatedAt', 'amount']

  const columnOptions: TableColumnOption[] = [
    { key: 'operationId', label: 'ID' },
    { key: 'humanCreatedAt', label: 'Created at' },
    { key: 'humanUpdatedAt', label: 'Updated at' },
    { key: 'operationType', label: 'Type' },
    { key: 'from', label: 'From' },
    { key: 'initialAmount', label: 'Initial amount' },
    { key: 'to', label: 'To' },
    { key: 'clientAmount', label: 'Client amount' },
    { key: 'operationStatus', label: 'Status' },
    { key: 'direction', label: 'Direction', defaultVisible: false },
    { key: 'sourceId', label: 'Source ID', defaultVisible: false },
    { key: 'sourceType', label: 'Source type', defaultVisible: false },
    { key: 'destinationId', label: 'Destination ID', defaultVisible: false },
    {
      key: 'destinationType',
      label: 'Destination type',
      defaultVisible: false,
    },
    { key: 'sourceAmount', label: 'Source amount', defaultVisible: false },
    { key: 'sourceCurrency', label: 'Source currency', defaultVisible: false },
    {
      key: 'destinationAmount',
      label: 'Destination amount',
      defaultVisible: false,
    },
    {
      key: 'destinationCurrency',
      label: 'Destination currency',
      defaultVisible: false,
    },
  ]

  const { settings, apply, resizeStart } = useTableColumnSettings({
    storageKey: 'crm:money-history-columns-layout',
    columns: columnOptions,
    defaultWidths: {
      operationId: 110,
      humanCreatedAt: 150,
      humanUpdatedAt: 150,
      operationType: 160,
      from: 180,
      initialAmount: 150,
      to: 180,
      clientAmount: 150,
      operationStatus: 150,
      direction: 180,
      sourceId: 110,
      sourceType: 150,
      destinationId: 130,
      destinationType: 160,
      sourceAmount: 140,
      sourceCurrency: 120,
      destinationAmount: 160,
      destinationCurrency: 140,
    },
  })

  const handleTableChange: TableProps<MoneyHistory>['onChange'] =
    useTableSearchParams({
      searchFields,
      filterFields,
      sorterFields,
      usePagination: true,
    })

  const csvContent = moneyHistories
    .map((item) => Object.values(item).join(','))
    .join('\n')

  const csvCode =
    'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

  const detailSearch = useColumnSearch<MoneyHistory>({
    dataIndex: 'operationId',
    placeholder: 'operation ID',
    customRender: (highlightedValue, record) => {
      const link = getOperationLink(record)
      const operationDetails = getOperationDetails(record)
      const fullLink = link ? `${window.location.origin}${link}` : ''

      const lines: string[] = []
      lines.push(`Client ID: ${entityId}`)
      if (client?.name) lines.push(`Name: ${client.name}`)
      if (client?.email) lines.push(`Email: ${client.email}`)
      if (client?.country) lines.push(`Country: ${client.country}`)
      lines.push(`Operation status: ${record.operationStatus}`)
      lines.push(`Type: ${record.operationType}`)
      if (fullLink) {
        lines.push(`Transaction ID: ${record.operationId} (${fullLink})`)
      } else {
        lines.push(`Transaction ID: ${record.operationId}`)
      }
      if (['deposit'].includes(record.operationType)) {
        lines.push(
          `View deposits: ${
            window.location.origin
          }${path.billing.deposit.entry()}?clientId=${entityId}`,
        )
        lines.push(`Payment method: ${record.fromName}`)
      }
      if (['withdrawal'].includes(record.operationType)) {
        lines.push(`Payment method: ${record.toName}`)
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>
            {link &&
            !(
              userRole != undefined &&
              userRole.indexOf(role.verificatorManager.verificatorAgent()) >= 0
            ) ? (
              <a href={link} target="_blank" rel="noreferrer">
                {highlightedValue}
              </a>
            ) : (
              highlightedValue
            )}
          </span>
          {operationDetails && (
            <span style={{ color: 'var(--crm-text-secondary)' }}>
              {operationDetails}
            </span>
          )}
          {['deposit', 'withdrawal', 'internal_transfer'].includes(
            record.operationType,
          ) && (
            <CopyOutlined
              style={{
                fontSize: 10,
                color: 'var(--crm-text-secondary)',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigator.clipboard.writeText(lines.join('\n'))
                message.success('Info copied')
              }}
            />
          )}
        </div>
      )
    },
  })

  const createdAtSearch = useColumnSearch<MoneyHistory>({
    dataIndex: 'humanCreatedAt',
    placeholder: 'humanCreatedAt',
  })

  const updatedAtSearch = useColumnSearch<MoneyHistory>({
    dataIndex: 'humanUpdatedAt',
    placeholder: 'humanUpdatedAt',
  })

  const columns: ColumnsType<MoneyHistory> = [
    {
      title: 'ID',
      key: 'operationId',
      dataIndex: 'operationId',
      sorter: (a, b) => parseInt(a.operationId) - parseInt(b.operationId),
      ...useColumnLocalSort<MoneyHistory>({
        dataIndex: 'operationId',
      }),
      ...detailSearch,
    },
    {
      title: 'Created at',
      key: 'humanCreatedAt',
      dataIndex: 'humanCreatedAt',
      ...useColumnLocalSort<MoneyHistory>({
        dataIndex: 'humanCreatedAt',
      }),
      ...createdAtSearch,
      sorter: (a, b) =>
        // @ts-ignore
        new Date(
          moment(a.humanCreatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL'),
        ) -
        // @ts-ignore
        new Date(
          moment(b.humanCreatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL'),
        ),
    },
    {
      title: 'Updated at',
      key: 'humanUpdatedAt',
      dataIndex: 'humanUpdatedAt',
      ...useColumnLocalSort<MoneyHistory>({
        dataIndex: 'humanUpdatedAt',
        initialValue: 'descend',
      }),
      ...updatedAtSearch,
      sorter: (a, b) =>
        // @ts-ignore
        new Date(
          moment(a.humanUpdatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL'),
        ) -
        // @ts-ignore
        new Date(
          moment(b.humanUpdatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL'),
        ),
    },
    {
      title: 'Type',
      key: 'operationType',
      dataIndex: 'operationType',
      filters: type === 'nova' ? NovaFilters : GeneralFilters,
      render: (item: string) => <Tag>{item}</Tag>,
      filterSearch: true,
      ...useColumnLocalFilter<MoneyHistory>({ dataIndex: 'operationType' }),
    },
    {
      title: 'From',
      key: 'from',
      render: (item: MoneyHistory) => (
        <>
          {item.operationType != 'internal_resale_buy'
            ? item.fromName
            : 'NOVA Wallet'}
        </>
      ),
    },

    {
      title: 'Initial amount',
      key: 'initialAmount',
      render: (item: MoneyHistory) => {
        if (item.direction == 'TRANSACTION_REQUEST_DIRECTION_IN') {
          return (
            <>
              {formatDigits(Number(item.destinationAmount))}{' '}
              {item.destinationCurrency}
            </>
          )
        } else {
          return (
            <>
              {formatDigits(Number(item.sourceAmount))} {item.sourceCurrency}
            </>
          )
        }
      },
    },
    {
      title: 'To',
      key: 'to',
      render: (item: MoneyHistory) => {
        return (
          <>
            {item.operationType != 'internal_resale_sell'
              ? item.toName
              : 'NOVA Wallet'}
          </>
        )
      },
    },
    {
      title: 'Client amount',
      key: 'clientAmount',
      render: (item: MoneyHistory) => {
        if (item.direction == 'TRANSACTION_REQUEST_DIRECTION_IN') {
          return (
            <>
              {formatDigits(Number(item.sourceAmount))} {item.sourceCurrency}
            </>
          )
        } else {
          return (
            <>
              {formatDigits(Number(item.destinationAmount))}{' '}
              {item.destinationCurrency}
            </>
          )
        }
      },
    },
    {
      title: 'Status',
      key: 'operationStatus',
      dataIndex: 'operationStatus',
      filters: moneyHistoryStatuses.map((status) => ({
        text: status.text,
        value: status.value,
      })),
      filterSearch: true,
      render: (item: string) => <Tag>{item}</Tag>,
      ...useColumnLocalFilter<MoneyHistory>({ dataIndex: 'operationStatus' }),
    },
    ...(columnOptions
      .filter((column) =>
        [
          'direction',
          'sourceId',
          'sourceType',
          'destinationId',
          'sourceAmount',
          'sourceCurrency',
          'destinationAmount',
          'destinationCurrency',
        ].includes(column.key),
      )
      .map((column) => ({
        title: column.label,
        key: column.key,
        dataIndex: column.key,
        width: 150,
        render: (value: unknown) =>
          column.key === 'direction' ? (
            <Tag
              color={
                value === transactionRequestDirection.In()
                  ? '#2eb85c'
                  : '#3399ff'
              }
              style={{ fontSize: 12 }}
            >
              {value === transactionRequestDirection.In() ? 'D' : 'W'}
            </Tag>
          ) : (
            String(value ?? '')
          ),
      })) as any),
  ]

  const configuredColumns = apply(
    columns.map((column) => {
      const rawColumn = column as any
      const key = String(
        rawColumn.key ?? rawColumn.dataIndex ?? rawColumn.title,
      )
      return {
        ...column,
        title: resizableTitle(rawColumn.title, key, resizeStart),
      }
    }),
  )

  useEffect(() => {
    getMoneyHistoryStatuses({})
  }, [])

  useEffect(() => {
    onChangeMoneyHistoriesList({
      clientId: entityId,
      limit: Number(searchParams.get('limit')) || 100,
      page: Number(searchParams.get('page')) || 1,
      order: searchParams.get('sorterField') ?? '-updatedAt',
      filters: getFilters(),
    })
  }, [searchParams])

  if (![0, 404, null].includes(errorMoneyHistoriesRequest?.code || null)) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          onChangeMoneyHistoriesList({
            clientId: entityId,
            limit: Number(searchParams.get('limit')) || 100,
            page: Number(searchParams.get('page')) || 1,
            order: searchParams.get('sorterField') ?? '-updatedAt',
            filters: getFilters(),
          })
          setMoneyHistoriesRequestError({
            message: '',
            code: 0,
          })
        }}
      />
    )
  }
  const current = Number(searchParams.get('page')) || 1
  const pageSize = Number(searchParams.get('limit')) || 100
  return (
    <CCardBody>
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}
      >
        {settings}
      </div>
      <Guard permission={'downloads_money_history'}>
        <CButton
          color="primary"
          className="mb-2"
          href={csvCode}
          download="coreui-table-data.csv"
          target="_blank"
        >
          Download current items (.csv)
        </CButton>
      </Guard>
      <Table
        dataSource={moneyHistories}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        columns={configuredColumns}
        rowKey={(record, index) => `${record.operationId}_${index}`}
        pagination={{
          current,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfMoneyHistories,
        }}
        onChange={handleTableChange}
      />
    </CCardBody>
  )
}
